import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { orderResourceTabs } from "@diality/contracts";
import * as React from "react";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "../components/ui/tabs";
import { Reveal } from "../components/ui/reveal";
import { cn } from "../lib/utils";
import { BlockLink, MediaSlotView } from "../render/context";
/** CMS-authored sections selected with the shared pill tabs. */
export function ResourceCardsView({ data, titleId, }) {
    const { heading, subheading, learnLabel = "Learn", supportLabel = "Support", resources, } = data;
    const sections = orderResourceTabs([
        {
            id: "learn",
            label: learnLabel,
            resources: resources.filter((r) => r.category !== "support"),
        },
        {
            id: "support",
            label: supportLabel,
            resources: resources.filter((r) => r.category === "support"),
        },
        ...(data.sections ?? []),
    ].filter((section) => section.resources.length > 0), data.tabOrder);
    const [selected, setSelected] = React.useState("");
    const active = sections.find((section) => section.id === selected) ?? sections[0];
    // The 1132px column: the padded frame holds the lockup and the switch, and the
    // cards sit BESIDE it, not inside it — in the prototype the card row is a
    // sibling of the frame, so it spans the full column rather than the frame's
    // 986px of content width.
    const column = cn("mx-auto flex w-full max-w-[1132px] flex-col items-center gap-6", "@min-[901px]:gap-14");
    const frame = (_jsxs("div", { className: cn("flex w-full flex-col items-center gap-8 rounded-[15px] px-6 py-10", "@min-[901px]:w-[1066px] @min-[901px]:max-w-[calc(100%-66px)]", "@min-[901px]:gap-10 @min-[901px]:px-10 @min-[901px]:py-0"), children: [(heading || subheading) && (_jsxs("div", { className: cn("flex w-full flex-col items-center gap-2 text-center", 
                // The lockup is allowed to spill 40px past the frame's padding on
                // either side, as it does in the prototype: the heading is set to
                // the inner column's width, not the padded frame's.
                "@min-[901px]:w-[1054px] @min-[901px]:max-w-[calc(100%+80px)]"), children: [heading && (_jsx(Reveal, { delay: 0.06, className: "w-full", children: _jsx("h2", { id: titleId, "data-resource-cards-title": true, className: cn("m-0 w-full text-balance font-display font-light tracking-[-0.02em] text-brand-blue", 
                            // Line height AFTER the size: tailwind-merge treats a
                            // `text-*` utility as able to carry line-height, so a
                            // `leading-*` declared before it is dropped.
                            "text-[clamp(40px,12cqw,56px)] leading-[1.04] @min-[901px]:text-[64px] @min-[901px]:leading-[1.04]"), children: heading }) })), subheading && (_jsx(Reveal, { delay: 0.12, className: "w-full", children: _jsx("p", { "data-resource-cards-description": true, className: cn("m-0 w-[696px] max-w-full mx-auto text-pretty font-sans font-light text-foreground", "text-lg leading-[1.5] @min-[901px]:text-xl @min-[901px]:leading-[1.55]"), children: subheading }) }))] })), sections.length > 1 && (_jsx("div", { className: "w-full min-w-0 overflow-x-auto", children: _jsx(TabsList, { label: "Choose a resource section", className: "mx-auto", children: sections.map((section) => (_jsx(TabsTrigger, { value: section.id, children: section.label }, section.id))) }) }))] }));
    if (sections.length > 1) {
        return (_jsxs(Tabs, { value: active?.id ?? "", onValueChange: setSelected, className: column, children: [frame, sections.map((section, index) => (_jsx(TabsPanel, { value: section.id, children: _jsx(ResourceCardGrid, { resources: section.resources, section: index + 1 }) }, section.id)))] }));
    }
    return (_jsxs("div", { className: column, children: [frame, _jsx(ResourceCardGrid, { resources: active?.resources ?? [], section: 1 })] }));
}
/**
 * One run of cards.
 *
 * Wrap-and-centre rather than a grid: a trailing partial row sits centred under
 * the full ones, which is what the prototype's Support group does with its five
 * cards. A third of the row is `calc((100% - 24px) / 3)` because the two 12px
 * column gaps come out of the width before it is divided.
 */
function ResourceCardGrid({ resources, section, }) {
    if (resources.length === 0) {
        return (_jsx("p", { "data-resource-cards-section": section, className: "w-full text-center text-muted-foreground", children: "No items found." }));
    }
    return (_jsx("div", { "data-resource-cards-section": section, className: cn("flex w-full flex-col items-stretch gap-2", "@min-[901px]:flex-row @min-[901px]:flex-wrap @min-[901px]:justify-center", "@min-[901px]:gap-x-3 @min-[901px]:gap-y-8"), children: resources.map((resource, index) => (_jsx(Reveal, { delay: 0.06 + index * 0.08, className: cn("flex min-w-0", "@min-[901px]:min-w-[360px] @min-[901px]:w-[calc((100%-24px)/3)] @min-[901px]:flex-[0_0_calc((100%-24px)/3)]"), children: _jsx(ResourceCard, { resource: resource, position: index + 1 }) }, `${resource.name}-${index}`))) }));
}
/**
 * Strips the parts of a URL a card does not show: `niddk.nih.gov`, not the
 * scheme. Used when the Editor typed no link text of their own.
 */
function linkLabel(url) {
    return url
        .replace(/^[a-z]+:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\/+$/, "");
}
function ResourceCard({ resource, position, }) {
    const { logo, name, description, linkText, url } = resource;
    return (_jsxs("article", { "data-resource-card": true, "data-resource-card-position": position, className: cn("flex w-full flex-1 flex-col items-start gap-4 rounded-[15px] bg-[#f6fafd]", "min-h-[250px] px-7 py-9", "@min-[901px]:min-h-0 @min-[901px]:px-10 @min-[901px]:py-12"), children: [_jsx(MediaSlotView, { image: logo, 
                // Only the empty-slot placeholder's shape. A supplied logo keeps its
                // own proportions inside the common height.
                ratio: "3 / 1", label: "Logo", className: "h-[41px] w-auto max-w-full shrink-0 rounded-none object-contain object-left", emptyRender: _jsx("span", { "aria-hidden": "true", "data-resource-card-logo-fallback": true, className: "size-[18px] shrink-0 rounded-full border-[5px] border-dashed border-brand-plum" }) }), _jsxs("div", { className: "flex w-full flex-col gap-2", children: [_jsx("h3", { "data-resource-card-name": true, className: "type-eyebrow-lg m-0 leading-[1.28] tracking-[0.1em] text-brand-ink-surface", children: name }), description && (_jsx("p", { "data-resource-card-summary": true, className: "m-0 text-pretty font-sans text-2xl font-light leading-[1.2] text-brand-ink-surface", children: description }))] }), url && (_jsxs(BlockLink, { href: url, target: "_blank", className: cn("mt-auto inline-flex items-center gap-2 pt-2", "font-sans text-sm font-bold uppercase leading-[14px] tracking-[0.1em]", "text-primary transition-colors hover:text-brand-blue-dark motion-reduce:transition-none"), children: [linkText || linkLabel(url), _jsx(ExternalLinkGlyph, {})] }))] }));
}
/** `currentColor` rather than the prototype's hard-coded blue, so the glyph
 *  follows the link through its hover state. */
function ExternalLinkGlyph() {
    return (_jsxs("svg", { "aria-hidden": "true", width: "15", height: "14", viewBox: "0 0 15 14", fill: "none", className: "block shrink-0", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M7.14233 0.700375C7.14236 0.313794 7.46254 -3.16231e-05 7.85703 2.39006e-09L14.2854 0.000483412C14.6798 0.000528313 14.9994 0.313972 14.9996 0.700375L14.9991 7.00085C14.9989 7.38724 14.6788 7.70029 14.2844 7.70025C13.8901 7.70005 13.5702 7.38682 13.5702 7.00036L13.5697 2.39065L7.29079 8.5437C7.01184 8.81702 6.55956 8.81704 6.28064 8.5437C6.00173 8.27034 6.00171 7.82713 6.28064 7.5538L12.5606 1.40075L7.85654 1.40027C7.46221 1.40015 7.14244 1.08679 7.14233 0.700375Z", fill: "currentColor" }), _jsx("path", { d: "M0 11.9001V2.10058C0 0.940835 0.95939 0.000678411 2.14286 0.000678411H4.64286V1.40061H2.14286C1.74837 1.40061 1.42857 1.714 1.42857 2.10058V11.9001C1.42857 12.2867 1.74837 12.6001 2.14286 12.6001H12.8571C13.2516 12.6001 13.5714 12.2867 13.5714 11.9001V10.1502H15V11.9001C15 13.0598 14.0406 14 12.8571 14H2.14286C0.95939 14 0 13.0598 0 11.9001Z", fill: "currentColor" })] }));
}
/**
 * The block as a page renders it.
 *
 * The band keeps its own horizontal gutters; BlockWrapper owns outer spacing.
 */
export function ResourceCardsBlock({ data, titleId, }) {
    return (_jsx("section", { 
        // Its OWN container, not one inherited from BlockRenderer. Callers reach
        // this block directly — its Storybook story does — with no `@container`
        // above it, and every `@min-[…]` below would silently never match: the
        // desktop layout would simply not exist there. Nesting inside
        // BlockRenderer's wrapper is harmless: both are the block's own width.
        className: "@container w-full bg-transparent", "aria-labelledby": titleId, children: _jsx("div", { className: cn("px-[6.667cqw]", "@min-[901px]:px-[clamp(24px,6cqw,50px)] @min-[1280px]:px-[clamp(24px,6.694cqw,154px)]"), children: _jsx(ResourceCardsView, { data: data, titleId: titleId }) }) }));
}
/**
 * Preserve the original two section lists and resolve additional named sections.
 * Resources reference stable section ids, so changing a label never moves cards.
 */
export function resourceCardsFromBlock(block, resolve) {
    const s = block.scalars ?? {};
    const cardsOf = (items, category) => (items ?? []).map((it) => ({
        logo: resolve(it.media?.logo),
        name: it.scalars?.name || "",
        description: it.scalars?.description || undefined,
        // A wordless link text is no link text: the card falls back to the
        // address-derived label rather than drawing a run of spaces.
        linkText: it.scalars?.linkText?.trim() || undefined,
        url: it.scalars?.url || undefined,
        category,
    }));
    return {
        tabOrder: s.tabOrder,
        heading: s.heading || undefined,
        subheading: s.subheading || s.description || undefined,
        learnLabel: s.sectionOneLabel?.trim() || undefined,
        supportLabel: s.sectionTwoLabel?.trim() || undefined,
        sections: (block.repeats?.sections ?? []).flatMap((section) => {
            const label = section.scalars?.label?.trim();
            if (!section.id || !label)
                return [];
            return [
                {
                    id: section.id,
                    label,
                    resources: cardsOf((block.repeats?.resources ?? []).filter((card) => card.scalars?.sectionId === section.id), "learn"),
                },
            ];
        }),
        resources: [
            ...cardsOf(block.repeats?.sectionOneResources, "learn"),
            ...cardsOf(block.repeats?.sectionTwoResources, "support"),
        ],
    };
}
export const resourceCardsBlockEntry = {
    Component: ResourceCardsBlock,
    fromBlock: resourceCardsFromBlock,
};
//# sourceMappingURL=resource-cards.js.map